import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { Search } from 'components/common';
import { DateRangeSelector } from 'components/common/charts';
import { Filters, FiltersActive } from 'components/common/filter';
import { AnalyticsPageFiltersContentTypes } from '.';

interface Props extends React.PropsWithChildren {
  additionalFilterIds?: string[];
  includeSearch?: string;
  includeSearchPlaceholder?: string;
}

const AnalyticsPageFilters = ({
  additionalFilterIds = [],
  children,
  includeSearch,
  includeSearchPlaceholder
}: Props) => {
  const { messages, theme } = useReduxState();

  const { pathname } = useRouter();
  const isActivityPage = /\/analytics(\?.*)?(?!\/.*)/.test(pathname);

  return (
    <div className="wrapper">
      <AnalyticsPageFiltersContentTypes />

      <div className="filters">
        <div className="filters-content">
          <div className="left-side">
            {includeSearch && (
              <div className="search">
                <Search
                  searchKey={includeSearch}
                  size="header"
                  withUrl={true}
                  {...(includeSearchPlaceholder
                    ? { placeholder: includeSearchPlaceholder }
                    : {})}
                />
              </div>
            )}

            <Filters
              allowMultiple={{
                accountAccess: true,
                accountIdentifier: true,
                accountLogin: true,
                accountType: true,
                groups: true,
                supervisors: true,
                userAttributes: true
              }}
              filterId="baseline"
              filters={{
                accountAccess: true,
                accountIdentifier: true,
                accountLogin: true,
                accountType: true,
                groups: true,
                supervisors: true,
                userAttributes: true
              }}
              triggerProps={{
                icon: 'user',
                text: messages.commonFiltersUsers
              }}
              hideSavedFilters={additionalFilterIds.length > 0}
            />

            {children}
          </div>

          <div className="right-side">
            <DateRangeSelector
              align="right"
              includeAllTimeOption={!isActivityPage}
              size="header"
              withUrl={true}
              {...(isActivityPage ? { initialDateRange: 'day' } : {})}
            />
          </div>
        </div>

        <FiltersActive filterIds={['baseline', ...additionalFilterIds]} />
      </div>

      <style jsx>{`
        .wrapper {
          background-color: white;
          border-radius: ${theme.cardBorderRadius};
          box-shadow: ${theme.cardBoxShadow};
          display: flex;
          flex-direction: column;
          margin-bottom: 30px;
          position: relative;
        }
        .filters {
          padding: 10px;
        }
        .filters-content {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          justify-content: space-between;
          width: 100%;
        }
        .left-side {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }
        .search {
          min-width: 220px;
        }
        @media (max-width: ${breakpoints.small}px) {
          .search {
            min-width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default AnalyticsPageFilters;
